import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, DollarSign, TrendingUp, Calendar, Award } from 'lucide-react';
import { useAuth, API } from '../App';
import { useCurrency } from '../components/CurrencyProvider';
import axios from 'axios';

export default function TrainerEarningsDetail() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { format: formatCurrency } = useCurrency();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const profileRes = await axios.get(`${API}/trainers/me`, { headers: { Authorization: `Bearer ${token}` } });
        const bookingsRes = await axios.get(`${API}/bookings/trainer/${profileRes.data.id}`, { headers: { Authorization: `Bearer ${token}` } });
        setBookings(bookingsRes.data);
      } catch (err) { console.error(err); }
      setLoading(false);
    };
    fetchData();
  }, [token]);

  const completedBookings = bookings.filter(b => b.training_status === 'completed');
  const totalEarned = completedBookings.reduce((sum, b) => sum + (b.amount || 0), 0);
  const avgPerTraining = completedBookings.length > 0 ? totalEarned / completedBookings.length : 0;
  const pendingEarnings = bookings.filter(b => ['confirmed', 'in_progress'].includes(b.training_status)).reduce((sum, b) => sum + (b.amount || 0), 0);

  // Monthly breakdown
  const monthlyData = {};
  completedBookings.forEach(b => {
    const month = new Date(b.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
    monthlyData[month] = (monthlyData[month] || 0) + (b.amount || 0);
  });

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full" /></div>;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <button onClick={() => navigate('/trainer/dashboard')} className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6 transition-colors" data-testid="back-to-dashboard">
          <ArrowLeft className="w-4 h-4" /> Back to Dashboard
        </button>

        <div className="mb-8">
          <h1 className="font-outfit text-3xl font-bold text-slate-900" data-testid="page-title">Earnings Overview</h1>
          <p className="text-slate-500 mt-1">Track your training income and earning trends</p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center"><DollarSign className="w-5 h-5 text-emerald-600" /></div>
              <span className="text-sm text-slate-500">Total Earned</span>
            </div>
            <div className="font-outfit text-3xl font-bold text-slate-900">{formatCurrency(totalEarned)}</div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center"><TrendingUp className="w-5 h-5 text-blue-600" /></div>
              <span className="text-sm text-slate-500">Avg per Training</span>
            </div>
            <div className="font-outfit text-3xl font-bold text-slate-900">{formatCurrency(avgPerTraining)}</div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center"><Calendar className="w-5 h-5 text-amber-600" /></div>
              <span className="text-sm text-slate-500">Pending Income</span>
            </div>
            <div className="font-outfit text-3xl font-bold text-amber-600">{formatCurrency(pendingEarnings)}</div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center"><Award className="w-5 h-5 text-purple-600" /></div>
              <span className="text-sm text-slate-500">Trainings Done</span>
            </div>
            <div className="font-outfit text-3xl font-bold text-slate-900">{completedBookings.length}</div>
          </div>
        </div>

        {/* Monthly Breakdown */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
          <h3 className="font-outfit font-semibold text-lg text-slate-900 mb-4">Monthly Earnings</h3>
          {Object.keys(monthlyData).length === 0 ? (
            <p className="text-slate-500 text-center py-8">No earnings yet. Complete trainings to see your income here.</p>
          ) : (
            <div className="space-y-3">
              {Object.entries(monthlyData).map(([month, amount]) => (
                <div key={month} className="flex items-center gap-4">
                  <span className="text-sm font-medium text-slate-600 w-24">{month}</span>
                  <div className="flex-1 bg-slate-100 rounded-full h-6 overflow-hidden">
                    <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 h-full rounded-full flex items-center justify-end pr-2 transition-all" style={{ width: `${Math.max(10, (amount / totalEarned) * 100)}%` }}>
                      <span className="text-xs text-white font-medium">{formatCurrency(amount)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Completed Trainings */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="p-4 border-b border-slate-200">
            <h3 className="font-semibold text-slate-900">Completed Training Income</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left p-4 text-sm font-medium text-slate-600">Company</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-600">Training</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-600">Date</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-600">Rating</th>
                  <th className="text-right p-4 text-sm font-medium text-slate-600">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {completedBookings.length === 0 ? (
                  <tr><td colSpan={5} className="p-12 text-center text-slate-500">No completed trainings yet</td></tr>
                ) : completedBookings.map(b => (
                  <tr key={b.id} className="hover:bg-slate-50">
                    <td className="p-4 font-medium text-slate-900">{b.corporate_name || 'Company'}</td>
                    <td className="p-4 text-sm text-slate-600">{b.job_title || 'Training'}</td>
                    <td className="p-4 text-sm text-slate-500">{new Date(b.created_at).toLocaleDateString()}</td>
                    <td className="p-4 text-sm text-slate-600">{b.feedback ? `${b.feedback.overall_rating}/5` : '-'}</td>
                    <td className="p-4 text-right font-medium text-emerald-600">{formatCurrency(b.amount || 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
